// =============================================================================
// FILE: components/session/SessionResumeBanner.tsx
// PURPOSE: R2 "Where You Left Off" Banner — shown at the top of the dashboard
//   when a user returns. R2 tells them exactly what they were doing, how long
//   ago, and offers a one-click resume.
//
//   Design: dismissable top banner, dark glass, teal accent.
//   Auto-dismisses after 12 seconds.
// =============================================================================

import { useEffect, useRef, useState, type JSX } from 'react';
import { X, RotateCcw, Clock, ArrowRight, Zap } from 'lucide-react';
import { useSessionResume, formatElapsed } from '../../stores/sessionResume.store.js';
import type { Route } from '../../App.js';

interface SessionResumeBannerProps {
  onNavigate: (route: Route) => void;
}

const ACCENT = '#25B5A8';

export function SessionResumeBanner({ onNavigate }: SessionResumeBannerProps): JSX.Element | null {
  const { shouldShowBanner, getLastSession, dismissBanner, snapshots } = useSessionResume();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const AUTO_DISMISS_MS = 12_000;

  useEffect(() => {
    const show = shouldShowBanner();
    if (!show) return;

    // Small delay so page renders first
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100);
      setProgress(pct);
      if (pct <= 0) {
        handleDismiss();
      }
    }, 80);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [visible]);

  const handleDismiss = () => {
    setVisible(false);
    if (timerRef.current) clearInterval(timerRef.current);
    dismissBanner();
  };

  const handleResume = (route: string) => {
    handleDismiss();
    onNavigate(route as Route);
  };

  if (!visible) return null;

  const lastSession = getLastSession();
  if (!lastSession) return null;

  const elapsed = formatElapsed(lastSession.timestamp);
  const recentRoutes = snapshots.slice(0, 3);

  return (
    <div
      data-testid="session-resume-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9200,
        animation: 'slideDown 0.35s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      {/* Progress bar */}
      <div style={{ height: 2, background: 'rgba(37,181,168,0.15)' }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: ACCENT,
          transition: 'width 0.08s linear',
        }} />
      </div>

      {/* Banner body */}
      <div style={{
        background: 'rgba(3,8,22,0.97)',
        borderBottom: '1px solid rgba(37,181,168,0.2)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}>
        {/* R2 indicator */}
        <div style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          background: 'rgba(37,181,168,0.12)',
          border: `1px solid rgba(37,181,168,0.3)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: ACCENT,
        }}>
          <RotateCcw size={14} />
        </div>

        {/* Main message */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#f0f4ff' }}>
              Welcome back.
            </span>
            <span style={{ fontSize: 12, color: 'rgba(148,172,210,0.55)' }}>
              You left off
            </span>
            <span style={{
              fontSize: 11, padding: '2px 7px', borderRadius: 5,
              background: 'rgba(37,181,168,0.1)', border: '1px solid rgba(37,181,168,0.25)',
              color: ACCENT, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Clock size={9} /> {elapsed}
            </span>
            <span style={{ fontSize: 12, color: 'rgba(148,172,210,0.55)' }}>in</span>
            <span style={{
              fontSize: 12, fontWeight: 700, color: '#dce8f8',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              {lastSession.routeIcon} {lastSession.routeLabel}
            </span>
            {lastSession.lastAction && (
              <>
                <span style={{ fontSize: 11, color: 'rgba(100,140,180,0.35)' }}>·</span>
                <span style={{ fontSize: 11, color: 'rgba(148,172,210,0.4)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                  Last: {lastSession.lastAction}
                </span>
              </>
            )}
          </div>

          {/* Recent route chips */}
          {recentRoutes.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {recentRoutes.map((s, i) => (
                <button
                  key={i}
                  data-testid={`resume-chip-${s.route}`}
                  onClick={() => handleResume(s.route)}
                  style={{
                    padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 10,
                    background: i === 0 ? `${ACCENT}14` : 'rgba(8,14,32,0.6)',
                    border: `1px solid ${i === 0 ? `${ACCENT}35` : 'rgba(30,50,80,0.5)'}`,
                    color: i === 0 ? '#5eead4' : 'rgba(148,172,210,0.55)',
                    fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                    transition: 'all 0.12s',
                  }}
                >
                  {s.routeIcon} {s.routeLabel}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Resume CTA */}
        <button
          data-testid="session-resume-btn"
          onClick={() => handleResume(lastSession.route)}
          style={{
            padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
            background: `${ACCENT}18`, border: `1px solid ${ACCENT}40`,
            color: '#5eead4', fontSize: 12, fontWeight: 700, flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.background = `${ACCENT}28`;
            (e.currentTarget as HTMLElement).style.boxShadow = `0 0 12px ${ACCENT}20`;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.background = `${ACCENT}18`;
            (e.currentTarget as HTMLElement).style.boxShadow = 'none';
          }}
        >
          <Zap size={12} /> Resume
          <ArrowRight size={11} />
        </button>

        {/* Dismiss */}
        <button
          data-testid="session-resume-dismiss"
          onClick={handleDismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 6,
            color: 'rgba(100,140,180,0.35)', flexShrink: 0, borderRadius: 6,
            transition: 'color 0.12s',
          }}
          onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(148,172,210,0.7)')}
          onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(100,140,180,0.35)')}
          title="Dismiss"
        >
          <X size={14} />
        </button>
      </div>

      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
      `}</style>
    </div>
  );
}
